/**
 * Hook pour recuperer les statistiques globales du dashboard admin
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AdminStats {
  mrr: number;
  totalUsers: number;
  totalEntreprises: number;
  totalChantiers: number;
  activationRate: number;
  alerts: Array<{ type: 'error' | 'warning'; message: string }>;
}

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Requetes paralleles pour les stats
      const [
        profilesResult,
        entreprisesResult,
        chantiersResult,
        subscriptionsResult,
        pastDueResult,
      ] = await Promise.all([
        // Total users
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        // Total entreprises
        supabase.from('entreprises').select('*', { count: 'exact', head: true }),
        // Chantiers ce mois
        supabase
          .from('chantiers')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()),
        // Subscriptions actives pour MRR
        supabase
          .from('subscriptions')
          .select('plan, billing_period, status')
          .in('status', ['active', 'trialing']),
        // Paiements echoues
        supabase
          .from('subscriptions')
          .select('entreprise_id, entreprises(nom)')
          .eq('status', 'past_due'),
      ]);

      const usersCount = profilesResult.count || 0;
      const entreprisesCount = entreprisesResult.count || 0;
      const chantiersCount = chantiersResult.count || 0;
      const subscriptionsData = subscriptionsResult.data || [];
      const pastDueSubscriptions = pastDueResult.data || [];

      // Calculer MRR
      const planPrices: Record<string, { monthly: number; yearly: number }> = {
        free: { monthly: 0, yearly: 0 },
        artisan: { monthly: 29, yearly: 290 },
        pro: { monthly: 79, yearly: 790 },
        entreprise: { monthly: 199, yearly: 1990 },
      };

      let mrr = 0;
      subscriptionsData.forEach((sub) => {
        const prices = planPrices[sub.plan] || { monthly: 0, yearly: 0 };
        if (sub.billing_period === 'yearly') {
          mrr += prices.yearly / 12;
        } else {
          mrr += prices.monthly;
        }
      });

      // Calculer taux d'activation (entreprises avec au moins 1 chantier)
      // On utilise le nombre d'entreprises par rapport au total
      const activationRate = entreprisesCount > 0 && chantiersCount > 0
        ? Math.min(100, Math.round((chantiersCount / entreprisesCount) * 30)) // Approximation
        : 0;

      // Construire les alertes
      const alerts: AdminStats['alerts'] = [];
      pastDueSubscriptions.forEach((sub) => {
        const entrepriseNom = (sub.entreprises as { nom?: string } | null)?.nom || 'Entreprise inconnue';
        alerts.push({
          type: 'error',
          message: `Paiement echoue - ${entrepriseNom}`,
        });
      });

      setStats({
        mrr: Math.round(mrr),
        totalUsers: usersCount,
        totalEntreprises: entreprisesCount,
        totalChantiers: chantiersCount,
        activationRate,
        alerts,
      });
    } catch (err) {
      console.error('[useAdminStats] Erreur:', err);
      setError(err instanceof Error ? err : new Error('Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return { stats, loading, error, refetch: fetchStats };
}
